import { usePage } from '@inertiajs/vue3';
import { computed, toValue } from 'vue';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { SharedData } from '@/types';

const DEFAULT_OG_IMAGE = '/og/default.png';

export interface MarketingHead {
    /** Full title including the site name, e.g. "Pricing | Tourium". Used for og:title. */
    title: string;
    /**
     * Bare title segment, e.g. "Pricing". Passed to Inertia's <Head>, which
     * appends the site name itself via the `title` callback in app.ts —
     * passing the full title there would render "Pricing | Tourium | Tourium".
     */
    documentTitle: string;
    description: string;
    /** Absolute canonical / og:url. */
    url: string;
    /** Absolute og:image URL. */
    image: string;
    /** Site name, used for og:site_name. */
    siteName: string;
}

export interface MarketingHeadParams {
    /** Page-specific title segment; the site name is appended automatically. */
    title: string;
    description: string;
    /** Path component (must start with '/') appended to appUrl. */
    path: string;
    /** OG image — absolute URL, or a root-relative path resolved against appUrl. */
    image?: string;
    siteName: string;
    appUrl: string;
}

/**
 * Pure builder for marketing-page head metadata. Kept separate from the
 * composable so it can be unit-tested without an Inertia page context.
 */
export function buildMarketingHead(params: MarketingHeadParams): MarketingHead {
    const appUrl = params.appUrl.replace(/\/+$/, '');
    const image = params.image && params.image.length > 0 ? params.image : DEFAULT_OG_IMAGE;

    return {
        title: `${params.title} | ${params.siteName}`,
        documentTitle: params.title,
        description: params.description,
        url: `${appUrl}${params.path}`,
        image: /^https?:\/\//.test(image) ? image : `${appUrl}${image}`,
        siteName: params.siteName,
    };
}

export interface UseMarketingHeadInput {
    title: MaybeRefOrGetter<string>;
    description: MaybeRefOrGetter<string>;
    /** Path component (starts with '/'), used for canonical + og:url. */
    path: MaybeRefOrGetter<string>;
    /** Optional OG image; defaults to /og/default.png. */
    image?: MaybeRefOrGetter<string | undefined>;
}

/**
 * Reactive marketing-page head metadata, sourced from the current page's
 * shared Inertia props (site name + app URL).
 */
export function useMarketingHead(input: UseMarketingHeadInput): ComputedRef<MarketingHead> {
    const page = usePage<SharedData>();

    return computed<MarketingHead>(() =>
        buildMarketingHead({
            title: toValue(input.title),
            description: toValue(input.description),
            path: toValue(input.path),
            image: input.image !== undefined ? toValue(input.image) : undefined,
            siteName: page.props.name,
            appUrl: page.props.appUrl ?? '',
        }),
    );
}
